import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { Users, Plus, Edit, Trash2, Mail, Shield, Calendar, UserPlus, Package, Copy, Check, BarChart3, PieChart, Archive, RotateCcw, RefreshCw } from 'lucide-react'
import InviteUserModal from '../Modals/InviteUserModal'
import EditUserModal from '../Modals/EditUserModal'
import ConfirmationModal from '../Modals/ConfirmationModal'
import { usersAPI } from '../../services/usersAPI'
import invitationsAPI from '../../services/invitationsAPI'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { useAuth } from '../../hooks/useAuth'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts'

const USERS_PAGE_SIZE = 10

function UsersListPagination({ page, pages, total, limit = USERS_PAGE_SIZE, onPageChange }) {
  if (!pages || pages <= 1) return null

  const start = ((page - 1) * limit) + 1
  const end = Math.min(page * limit, total)

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
      <p className="text-sm text-gray-700">
        Showing {start} to {end} of {total} results
      </p>
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

export default function UsersTab({ organizationId }) {
  const { hasPermission } = useAuth()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [invitationToDelete, setInvitationToDelete] = useState(null)
  const [selectedProductId, setSelectedProductId] = useState('')
  const [copiedInvitationId, setCopiedInvitationId] = useState(null)
  const [monthFilter, setMonthFilter] = useState(12) // Default last 12 months
  const [activeSection, setActiveSection] = useState('active') // 'active', 'pending', 'archived'
  const [activePage, setActivePage] = useState(1)
  const [archivedPage, setArchivedPage] = useState(1)
  const [pendingPage, setPendingPage] = useState(1)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [userToRestore, setUserToRestore] = useState(null)
  const queryClient = useQueryClient()

  // Fetch organization subscriptions - optimized caching
  const { data: subscriptionsData, isLoading: subscriptionsLoading } = useQuery(
    ['subscriptions', organizationId],
    () => api.get(`/subscriptions/organization/${organizationId}`).then(r => r.data),
    { 
      enabled: !!organizationId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  const subscriptions = subscriptionsData?.data?.subscriptions || []
  const subscribedProducts = subscriptions.map(sub => ({
    id: sub.product_id,
    name: sub.product_name,
    subscription_id: sub.id
  }))

  // One page of organization users. Charts use summary counts from the same response.
  const { data: usersData, isLoading: usersLoading, isFetching: usersFetching, refetch: refetchUsers, error: usersError } = useQuery(
    ['users', organizationId, activePage, selectedProductId],
    () => usersAPI.getByOrganization(organizationId, {
      page: activePage,
      limit: USERS_PAGE_SIZE,
      product_id: selectedProductId || undefined,
      tz_offset: new Date().getTimezoneOffset()
    }),
    { 
      enabled: !!organizationId,
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      refetchOnMount: true,
      onError: (error) => {
        console.error('Error fetching users:', error);
      }
    }
  )
  const users = usersData?.data?.data?.users || []
  const usersPagination = usersData?.data?.data?.pagination || {}
  const userSummary = usersData?.data?.data?.summary || null

  // Fetch pending invitations - optimized caching
  const { data: invitationsData, isLoading: invitationsLoading, isFetching: invitationsFetching, refetch: refetchInvitations } = useQuery(
    ['invitations', organizationId],
    () => invitationsAPI.getByOrganization(organizationId, 'pending'),
    { 
      enabled: !!organizationId,
      staleTime: 2 * 60 * 1000, // 2 minutes (invitations change more frequently)
      cacheTime: 10 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  const invitations = invitationsData?.data?.invitations || []

  // Archived users for the current page only
  const { data: archivedUsersData, isLoading: archivedUsersLoading, isFetching: archivedUsersFetching, refetch: refetchArchivedUsers } = useQuery(
    ['archived-users', organizationId, archivedPage, selectedProductId],
    () => usersAPI.getArchivedByOrganization(organizationId, {
      page: archivedPage,
      limit: USERS_PAGE_SIZE,
      product_id: selectedProductId || undefined
    }),
    { 
      enabled: !!organizationId && activeSection === 'archived',
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      refetchOnMount: true,
    }
  )
  const archivedUsers = archivedUsersData?.data?.data?.users || []
  const archivedPagination = archivedUsersData?.data?.data?.pagination || {}

  const isRefreshingUsers = (usersFetching || invitationsFetching || archivedUsersFetching)
    && !usersLoading && !invitationsLoading && !archivedUsersLoading

  const handleRefreshUsers = () => {
    refetchUsers()
    refetchInvitations()
    if (activeSection === 'archived') refetchArchivedUsers()
  }
  
  // Helper function to check if an invitation matches the selected product
  const invitationMatchesProduct = (invitation) => {
    if (!selectedProductId) return true
    
    try {
      // Check if invitation's product_id matches (convert to string for comparison)
      if (invitation.product_id && 
          String(invitation.product_id) === String(selectedProductId)) {
        return true
      }
      
      // Check if invitation's products_data includes this product
      let productsData = invitation.products_data
      // Parse if it's a string (JSONB from database)
      if (typeof productsData === 'string') {
        try {
          productsData = JSON.parse(productsData)
        } catch (e) {
          productsData = []
        }
      }
      
      if (productsData && Array.isArray(productsData)) {
        return productsData.some(product => 
          product && product.product_id && 
          String(product.product_id) === String(selectedProductId)
        )
      }
      
      return false
    } catch (error) {
      console.error('Error filtering invitation:', error, invitation)
      return false
    }
  }
  
  // Filter pending invitations by selected product. User rows are already paged by the API.
  const filteredInvitations = selectedProductId
    ? invitations.filter(invitationMatchesProduct)
    : invitations

  const pendingPages = Math.max(1, Math.ceil(filteredInvitations.length / USERS_PAGE_SIZE))
  const currentPendingPage = Math.min(pendingPage, pendingPages)
  const pendingStart = (currentPendingPage - 1) * USERS_PAGE_SIZE
  const pagedInvitations = filteredInvitations.slice(pendingStart, pendingStart + USERS_PAGE_SIZE)

  const changeSection = (section) => {
    setActiveSection(section)
  }

  useEffect(() => {
    const pages = usersPagination.pages
    if (pages && activePage > pages) setActivePage(pages)
  }, [usersPagination.pages, activePage])

  useEffect(() => {
    const pages = archivedPagination.pages
    if (pages && archivedPage > pages) setArchivedPage(pages)
  }, [archivedPagination.pages, archivedPage])

  // Debug logging
  console.log('UsersTab Debug:', {
    organizationId,
    usersData,
    users,
    usersLoading,
    usersError
  });
  
  // Check the actual response structure
  if (usersData) {
    console.log('Full usersData:', usersData);
    console.log('usersData.data:', usersData.data);
    console.log('usersData.data.data:', usersData.data?.data);
    console.log('usersData.data.data.users:', usersData.data?.data?.users);
  }

  // Delete user mutation
  const deleteMutation = useMutation(
    (userId) => usersAPI.delete(userId),
    {
      onSuccess: () => {
        toast.success('User archived successfully!')
        queryClient.invalidateQueries(['users', organizationId])
        queryClient.invalidateQueries(['archived-users', organizationId])
      },
      onError: () => {
        toast.error('Failed to archive user')
      }
    }
  )

  // Delete invitation mutation
  const deleteInvitationMutation = useMutation(
    (invitationId) => invitationsAPI.delete(invitationId),
    {
      onSuccess: () => {
        toast.success('Invitation deleted successfully!')
        queryClient.invalidateQueries(['invitations', organizationId])
        queryClient.invalidateQueries(['acceptedInvitations', organizationId])
      },
      onError: () => {
        toast.error('Failed to delete invitation')
      }
    }
  )

  // Restore user mutation
  const restoreUserMutation = useMutation(
    (userId) => usersAPI.updateStatus(userId, 'active'),
    {
      onSuccess: () => {
        toast.success('User restored successfully!')
        queryClient.invalidateQueries(['users', organizationId])
        queryClient.invalidateQueries(['archived-users', organizationId])
        setShowRestoreModal(false)
        setUserToRestore(null)
      },
      onError: (error) => {
        console.error('Restore user error:', error)
        const errorMessage = error.response?.data?.message || 'Failed to restore user. Please try again.'
        toast.error(errorMessage)
      }
    }
  )

  const handleEditUser = (user) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleEditSuccess = () => {
    setShowEditModal(false)
    setSelectedUser(null)
    queryClient.invalidateQueries(['users', organizationId])
    queryClient.invalidateQueries(['acceptedInvitations', organizationId])
  }

  const handleDeleteUser = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName })
    setShowDeleteConfirmation(true)
  }

  const confirmDeleteUser = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id)
      setShowDeleteConfirmation(false)
      setUserToDelete(null)
    }
  }

  const handleDeleteInvitation = (invitation) => {
    setInvitationToDelete(invitation)
    setShowDeleteConfirmation(true)
  }

  const confirmDeleteInvitation = () => {
    if (invitationToDelete) {
      deleteInvitationMutation.mutate(invitationToDelete.id)
      setShowDeleteConfirmation(false)
      setInvitationToDelete(null)
    }
  }

  const handleRestoreUser = (userId, userName) => {
    setUserToRestore({ id: userId, name: userName })
    setShowRestoreModal(true)
  }

  const confirmRestoreUser = () => {
    if (userToRestore) {
      restoreUserMutation.mutate(userToRestore.id)
    }
  }

  const cancelRestoreUser = () => {
    setShowRestoreModal(false)
    setUserToRestore(null)
  }

  // Get invitation link
  const getInvitationLink = (token) => {
    // Use frontend URL for invitation links (not backend API URL)
    const frontendUrl = window.location.origin || 'http://localhost:3000'
    return `${frontendUrl}/accept-invitation/${token}`
  }

  // Copy invitation link
  const copyInvitationLink = (token, invitationId) => {
    const link = getInvitationLink(token)
    navigator.clipboard.writeText(link).then(() => {
      setCopiedInvitationId(invitationId)
      toast.success('Invitation link copied to clipboard!')
      setTimeout(() => setCopiedInvitationId(null), 2000)
    }).catch(() => {
      toast.error('Failed to copy link')
    })
  }

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never'
    
    try {
      const date = new Date(lastLogin)
      
      if (isNaN(date.getTime())) {
        return 'Invalid date'
      }
      
      const now = new Date()
      const diffInMs = now - date
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
      
      if (diffInMinutes < 1) return 'Just now'
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`
      if (diffInHours < 24) return `${diffInHours}h ago`
      if (diffInDays < 7) return `${diffInDays}d ago`
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`
      if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      console.error('Error formatting last login:', error)
      return 'Invalid date'
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' },
      archived: { color: 'bg-gray-200 text-gray-700', label: 'Archived' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  // Process monthly additions data (users + invitations)
  const monthlyUserData = useMemo(() => {
    const now = new Date()
    const months = []

    for (let i = monthFilter - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      months.push({ month: monthLabel, monthKey, count: 0 })
    }

    ;(userSummary?.monthly || []).forEach((entry) => {
      const monthEntry = months.find(m => m.monthKey === entry.month)
      if (monthEntry) monthEntry.count += Number(entry.count) || 0
    })

    filteredInvitations.forEach(invitation => {
      if (invitation.created_at) {
        const invDate = new Date(invitation.created_at)
        if (!isNaN(invDate.getTime())) {
          const monthKey = `${invDate.getFullYear()}-${String(invDate.getMonth() + 1).padStart(2, '0')}`
          const monthEntry = months.find(m => m.monthKey === monthKey)
          if (monthEntry) monthEntry.count++
        }
      }
    })

    return months
  }, [userSummary, filteredInvitations, monthFilter])

  // Process status distribution data
  const { statusData, activeUserCount, pendingUserCount, archivedUserCount, totalUserCount } = useMemo(() => {
    const activeCount = userSummary ? userSummary.active : users.filter(user => user.status === 'active').length
    const pendingCount = filteredInvitations.length
    const archivedCount = userSummary
      ? userSummary.archived
      : (archivedPagination.total || archivedUsers.length)
    const totalCount = activeCount + pendingCount + archivedCount

    return {
      statusData: [
        { name: 'Active', value: activeCount, color: '#10B981' },
        { name: 'Pending', value: pendingCount, color: '#F59E0B' },
        { name: 'Archived', value: archivedCount, color: '#6B7280' }
      ].filter(item => item.value > 0),
      activeUserCount: activeCount,
      pendingUserCount: pendingCount,
      archivedUserCount: archivedCount,
      totalUserCount: totalCount
    }
  }, [userSummary, users, filteredInvitations, archivedPagination.total, archivedUsers.length])

  if (usersLoading || invitationsLoading || subscriptionsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const refreshButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleRefreshUsers}
      disabled={isRefreshingUsers}
      title="Refresh users list"
    >
      <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshingUsers ? 'animate-spin' : ''}`} />
      Refresh
    </Button>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Organization Users</h3>
          <p className="text-sm text-gray-600">
            Manage users who have access to this organization
          </p>
        </div>
        {hasPermission('organizations.read') && hasPermission('organization.create') && (
          <Button onClick={() => setShowInviteModal(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Users
          </Button>
        )}
      </div>

      {/* Product Filter */}
      {subscribedProducts.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Package className="h-5 w-5 text-gray-500 mr-2" />
                <label className="text-sm font-medium text-gray-700">
                  Filter by Product:
                </label>
              </div>
              <select
                value={selectedProductId}
                onChange={(e) => {
                  setSelectedProductId(e.target.value)
                  setActivePage(1)
                  setArchivedPage(1)
                  setPendingPage(1)
                }}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">All Products</option>
                {subscribedProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              {selectedProductId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedProductId('')
                    setActivePage(1)
                    setArchivedPage(1)
                    setPendingPage(1)
                  }}
                  className="text-sm"
                >
                  Clear Filter
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Users Added by Month
              </CardTitle>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Last</label>
                <select
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(Number(e.target.value))}
                  className="input text-sm min-w-[8rem]"
                >
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                  <option value={24}>24 months</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyUserData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#3B82F6" name="Users/Invites" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <PieChart className="h-5 w-5 mr-2" />
                User Status Distribution
              </CardTitle>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-500">Total Users</span>
                  <span className="text-gray-900 font-semibold">{totalUserCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 font-medium text-gray-500">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    Active
                  </span>
                  <span className="text-gray-900 font-semibold">{activeUserCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 font-medium text-gray-500">
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    Pending
                  </span>
                  <span className="text-gray-900 font-semibold">{pendingUserCount}</span>
                </div>
                {archivedUserCount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 font-medium text-gray-500">
                      <span className="h-2 w-2 rounded-full bg-gray-500" />
                      Archived
                    </span>
                    <span className="text-gray-900 font-semibold">{archivedUserCount}</span>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) =>
                    `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900">{totalUserCount}</p>
              </div>
              <div className="rounded-lg border border-green-100 bg-green-50/50 p-4">
                <p className="text-xs uppercase tracking-wide text-green-600">Active Users</p>
                <p className="text-2xl font-semibold text-green-700">{activeUserCount}</p>
              </div>
              <div className="rounded-lg border border-yellow-100 bg-yellow-50/50 p-4">
                <p className="text-xs uppercase tracking-wide text-yellow-600">Pending Invites</p>
                <p className="text-2xl font-semibold text-yellow-700">{pendingUserCount}</p>
              </div>
              {archivedUserCount > 0 && (
                <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-600">Archived Users</p>
                  <p className="text-2xl font-semibold text-gray-700">{archivedUserCount}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => changeSection('active')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeSection === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Active ({activeUserCount})
          </button>
          <button
            onClick={() => changeSection('pending')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeSection === 'pending'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-yellow-600 hover:border-yellow-300'
            }`}
          >
            <Mail className="h-4 w-4 inline mr-2" />
            Pending ({pendingUserCount})
          </button>
          <button
            onClick={() => changeSection('archived')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeSection === 'archived'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-red-600 hover:border-red-300'
            }`}
          >
            <Archive className="h-4 w-4 inline mr-2" />
            Archived ({archivedUserCount})
          </button>
        </nav>
      </div>

      {/* Pending Invitations */}
      {activeSection === 'pending' && filteredInvitations.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Pending Invitations
              {selectedProductId && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({subscribedProducts.find(p => p.id === selectedProductId)?.name})
                </span>
              )}
            </CardTitle>
            {refreshButton}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invited
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedInvitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{invitation.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{invitation.role_name || 'Default'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(invitation.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(invitation.expires_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyInvitationLink(invitation.token, invitation.id)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Copy invitation link"
                          >
                            {copiedInvitationId === invitation.id ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                          {hasPermission('organizations.read') && hasPermission('organization.delete') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteInvitation(invitation)}
                              disabled={deleteInvitationMutation.isLoading}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete Invitation"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <UsersListPagination
              page={currentPendingPage}
              pages={pendingPages}
              total={filteredInvitations.length}
              onPageChange={setPendingPage}
            />
          </CardContent>
        </Card>
      )}

      {/* Active Users List */}
      {activeSection === 'active' && users.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Active Users
              {selectedProductId && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({subscribedProducts.find(p => p.id === selectedProductId)?.name})
                </span>
              )}
            </CardTitle>
            {refreshButton}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {(user.full_name || user.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name || 'No name'}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            // Helper function to determine role priority
                            const getRolePriority = (roleName) => {
                              if (!roleName) return 0;
                              const normalized = roleName.toLowerCase().trim();
                              if (normalized.includes('super admin') || normalized.includes('superadmin')) return 100;
                              if (normalized.includes('admin')) return 90;
                              if (normalized.includes('manager')) return 70;
                              if (normalized.includes('employee')) return 50;
                              if (normalized.includes('user')) return 30;
                              if (normalized.includes('reader') || normalized.includes('viewer')) return 20;
                              return 10;
                            };
                            
                            // If product filter is active, show role for that product
                            if (selectedProductId && user.product_roles) {
                              const productRole = user.product_roles.find(pr => 
                                String(pr.product_id) === String(selectedProductId)
                              );
                              if (productRole) return productRole.role_name;
                            }
                            
                            // Otherwise, show highest priority role from all product roles
                            if (user.product_roles && user.product_roles.length > 0) {
                              const highestRole = user.product_roles.reduce((highest, current) => {
                                const currentPriority = getRolePriority(current.role_name);
                                const highestPriority = getRolePriority(highest.role_name);
                                return currentPriority > highestPriority ? current : highest;
                              });
                              return highestRole.role_name;
                            }
                            
                            // Fallback to user.role or default
                            return user.role || 'User';
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatLastLogin(user.last_login || user.lastLogin)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {hasPermission('organizations.read') && hasPermission('organization.update') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Edit User"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {hasPermission('organizations.read') && hasPermission('organization.delete') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id, user.full_name || user.email)}
                              disabled={deleteMutation.isLoading}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete User"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <UsersListPagination
              page={activePage}
              pages={usersPagination.pages || 1}
              total={usersPagination.total || users.length}
              limit={usersPagination.limit || USERS_PAGE_SIZE}
              onPageChange={setActivePage}
            />
          </CardContent>
        </Card>
      )}

      {/* Archived Users List */}
      {activeSection === 'archived' && archivedUsersLoading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {activeSection === 'archived' && archivedUsers.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center">
              <Archive className="h-5 w-5 mr-2" />
              Archived Users
              {selectedProductId && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({subscribedProducts.find(p => p.id === selectedProductId)?.name})
                </span>
              )}
            </CardTitle>
            {refreshButton}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archived User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archived Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {archivedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {(user.full_name || user.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name || 'No name'}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            // Helper function to determine role priority
                            const getRolePriority = (roleName) => {
                              if (!roleName) return 0;
                              const normalized = roleName.toLowerCase().trim();
                              if (normalized.includes('super admin') || normalized.includes('superadmin')) return 100;
                              if (normalized.includes('admin')) return 90;
                              if (normalized.includes('manager')) return 70;
                              if (normalized.includes('employee')) return 50;
                              if (normalized.includes('user')) return 30;
                              if (normalized.includes('reader') || normalized.includes('viewer')) return 20;
                              return 10;
                            };
                            
                            // If product filter is active, show role for that product
                            if (selectedProductId && user.product_roles) {
                              const productRole = user.product_roles.find(pr => 
                                String(pr.product_id) === String(selectedProductId)
                              );
                              if (productRole) return productRole.role_name;
                            }
                            
                            // Show highest priority role from all product roles
                            if (user.product_roles && user.product_roles.length > 0) {
                              const highestRole = user.product_roles.reduce((highest, current) => {
                                const currentPriority = getRolePriority(current.role_name);
                                const highestPriority = getRolePriority(highest.role_name);
                                return currentPriority > highestPriority ? current : highest;
                              });
                              return highestRole.role_name;
                            }
                            
                            // Fallback to user.role or default
                            return user.role || 'User';
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreUser(user.id, user.full_name || user.email)}
                            disabled={restoreUserMutation.isLoading}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Restore User"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <UsersListPagination
              page={archivedPage}
              pages={archivedPagination.pages || 1}
              total={archivedPagination.total || archivedUsers.length}
              limit={archivedPagination.limit || USERS_PAGE_SIZE}
              onPageChange={setArchivedPage}
            />
          </CardContent>
        </Card>
      )}

      {/* No Users Message */}
      {activeSection === 'active' && users.length === 0 && !usersLoading && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Active Users
            </CardTitle>
            {refreshButton}
          </CardHeader>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Yet</h3>
            <p className="text-gray-600 mb-4">Invite users to join this organization.</p>
            {hasPermission('organizations.read') && hasPermission('organization.create') && (
              <Button onClick={() => setShowInviteModal(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Invite First User
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* No Archived Users Message */}
      {activeSection === 'archived' && archivedUsers.length === 0 && !archivedUsersLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <Archive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Archived Users</h3>
            <p className="text-gray-600">There are no archived users for this organization.</p>
          </CardContent>
        </Card>
      )}

      {/* No Pending Invitations Message */}
      {activeSection === 'pending' && filteredInvitations.length === 0 && !invitationsLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Invitations</h3>
            <p className="text-gray-600 mb-4">All invitations have been accepted or expired.</p>
            <Button onClick={() => setShowInviteModal(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Users
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        organizationId={organizationId}
        onSuccess={() => {
          setShowInviteModal(false)
          queryClient.invalidateQueries(['invitations', organizationId])
        }}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedUser(null)
        }}
        user={selectedUser}
        organizationId={organizationId}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Modal (reused for users and invitations) */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false)
          setUserToDelete(null)
          setInvitationToDelete(null)
        }}
        onConfirm={() => {
          if (userToDelete) return confirmDeleteUser()
          if (invitationToDelete) return confirmDeleteInvitation()
        }}
        title={userToDelete ? 'Delete User' : 'Delete Invitation'}
        message={userToDelete
          ? `Are you sure you want to delete User "${userToDelete?.name}"?`
          : `Are you sure you want to delete the invitation for "${invitationToDelete?.email}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Restore User Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRestoreModal}
        onClose={cancelRestoreUser}
        onConfirm={confirmRestoreUser}
        title="Restore User"
        message={`Are you sure you want to restore User "${userToRestore?.name}"? `}
        confirmText={restoreUserMutation.isLoading ? "Restoring..." : "Restore"}
        cancelText="Cancel"
        type="info"
      />
    </div>
  )
}
